import { IInstructorResponse } from './apiResponseTypes';
import { BaseApiClient } from './BaseApiClient';
import { IMakeBookingArguments } from './booking-interfaces';
import { IApiInterface } from './IApiConfig';
import { IAuthTokenBuilder } from './IAuthTokenBuilder';

export class BookingServiceClient extends BaseApiClient {
  public constructor(
    config: IApiInterface,
    fetch: any,
    authTokenBuilder: IAuthTokenBuilder,
    googleAnalytics?: any,
  ) {
    super(config, fetch, authTokenBuilder, googleAnalytics);
  }

  public getNextCheckinBookingDetailsForUser = async (uid: string, gymName: string) => {
    const result = await this.fetch(`${this.config.LOCAL_SERVER_BASE_URL}/user/${uid}/${gymName}/checkin`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
    const data = await result.json();
    if (result.status === 404) {
      return null;
    }
    if (result.status > 200) {
      throw new Error(data.message);
    }
    return data;
  };

  public async getMyBookings(userProgram: any) {
    const queryString = `userProgram=${userProgram}`;
    const request = await this.buildGetRequest(`api/users/me/bookings?${queryString}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getInfluencerBookings() {
    const request = await this.buildGetRequest('api/users/me/influencer/bookings');
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getMyWorkoutHistory() {
    const requestTimeQuery = `requestTime=${Date.now()}`;
    const request = await this.buildGetRequest(`api/users/me/workoutHistory?${requestTimeQuery}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getAvailableZoneBookingsOnYYYYMMDD = async (withPartner: boolean, yyyymmdd: string, gymName: string) => {
    const withPartnerQueryString = withPartner ? '&withPartner=true' : '';
    const queryString = `yyyymmdd=${yyyymmdd}${withPartnerQueryString}`;
    const request = await this.buildGetRequest(`api/bookings/${gymName}/all-available-time-slots?${queryString}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getInfluencerAvailableBookings = async (uid: string) => {
    const request = await this.buildGetRequest(`api/influencers/${uid}/bookings`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getHomeAvailableBookings = async (from: string, gymName: string, timezone: string) => {
    const timezoneQuery = timezone ? `timezone=${timezone}` : '';
    const queryString = `fromDate=${from}&gymName=${gymName}&${timezoneQuery}`;
    const request = await this.buildGetRequest(`api/home-time-slots?${queryString}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getHydratedHomeWorkout = async (
    workoutId: string,
    timezone: string,
    playbackSpeed?: number,
  ) => {
    const deviceTime = Date.now();
    const timezoneQuery = timezone ? `&timezone=${timezone}` : '';
    const playbackSpeedQueryVariable = playbackSpeed ? `&playbackSpeed=${playbackSpeed}` : '';
    const request = await this.buildGetRequest(
      `api/bookings/${workoutId}/hydrated-home-workout?`
        + `userDeviceEpochTimeAsString=${deviceTime}${timezoneQuery}${playbackSpeedQueryVariable}`,
    );
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public resumeHomeWorkout = async (workoutId: string, pauseStartTime: Date, pauseResumeTime: Date) => {
    const request = await this.buildPutRequest(`api/bookings/${workoutId}/resume-home-workout`, {
      pauseStartTime,
      pauseResumeTime,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getGenericTrackListFor = async (cardioOrResistance: string) => {
    const request = await this.buildGetRequest(`api/music/${cardioOrResistance}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public makeBooking = async (options: IMakeBookingArguments) => {
    const playbackSpeedQueryVariable = options.playbackSpeed ? `playbackSpeed=${options.playbackSpeed}` : '';
    const request = await this.buildPostRequest(`api/bookings?${playbackSpeedQueryVariable}`, options);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getBookingsForDay = async (date: string) => {
    const request = await this.buildGetRequest(`api/bookings?date=${date}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  };

  public checkIn = async (gymName: string) => {
    const request = await this.buildPostRequest('api/bookings/checkin', {
      gymName,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public makeBookingWithPartnerAndInvite = async (
    partnerId: string,
    startDateUTC: number,
    workoutType: number,
    gymName: string,
    workoutSettings: any,
    areaCategory: string,
  ) => {
    const request = await this.buildPostRequest('api/bookings/?withPartnerAndInvite=1', {
      partnerId,
      startDateUTC,
      workoutType,
      gymName,
      workoutSettings,
      areaCategory,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    this.sendAnalyticsEventAndParams('make_invite', {
      analyticsDate: this.buildAnalyticsDate(startDateUTC, workoutType),
      workoutType,
      partnerId,
      gymName,
    });
    return data;
  };

  public cancelBookingAndReturnBookingList = async (workoutSessionId: string) => {
    const request = await this.buildDeleteRequest(`api/bookings/${workoutSessionId}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public cancelBooking = async (uid: string, workoutSessionId: string) => {
    await this.cancelBookingAndReturnBookingList(workoutSessionId);
    return workoutSessionId;
  };

  public changeBooking = async (
    uid: string,
    workoutSessionId: string,
    changeToDateUTC: number,
    workoutType: number,
    gymName: string,
    workoutSettings?: any,
    isStudioDemo?: boolean,
  ) => {
    const request = await this.buildPostRequest(`api/bookings/${workoutSessionId}`, {
      changeToDateUTC,
      workoutType,
      gymName,
      workoutSettings,
      isStudioDemo,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    this.sendAnalyticsEventAndParams('change_booking', {
      analyticsDate: this.buildAnalyticsDate(changeToDateUTC, workoutType),
      workoutType,
      gymName,
    });
    return data;
  };

  public resetWorkoutSettings = async (
    workoutSessionId: string,
    settings: any,
    shouldExcludeFromProgram: boolean,
    shouldSetUserDefaultEquipment: boolean,
  ) => {
    const request = await this.buildPostRequest(`api/bookings/${workoutSessionId}/settings`, {
      settings,
      shouldExcludeFromProgram,
      shouldSetUserDefaultEquipment,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public findPartnerForBooking = async (
    partnerIdentifer: string,
    workoutType: number,
    workoutStartTime: number,
    gymName: string,
  ) => {
    const query = `partnerIdentifer=${encodeURIComponent(partnerIdentifer)}`
      + `&workoutType=${workoutType}`
      + `&workoutStartTime=${workoutStartTime}`
      + `&gymName=${gymName}`;
    const request = await this.buildGetRequest(`api/users/availability?${query}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public findWhitelistedPartnerForBooking = async (workoutType: number, workoutStartTime: number, gymName: string) => {
    const query = 'whitelistedPartners=true'
    + `&workoutType=${workoutType}`
    + `&workoutStartTime=${workoutStartTime}`
    + `&gymName=${gymName}`;
    const request = await this.buildGetRequest(`api/users/availability?${query}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getRecommendedSettingsForDate = async (workoutDate: number, userProgram: any, gymName: string) => {
    const queryString = `workoutDate=${workoutDate}&userProgram=${userProgram}&gymName=${gymName}`;
    const request = await this.buildGetRequest(`api/users/me/bookings/recommendedSettings?${queryString}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public async getLastWeekWorkoutHistory(from: number) {
    const queryString = `from=${from}`;
    const request = await this.buildGetRequest(`api/users/me/lastweekHistory?${queryString}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async completeWorkout(
    workoutId: string,
    metricResult: any,
    heartRateDataPoints: any,
  ) {
    const request = await this.buildPostRequest(`api/bookings/${workoutId}/complete`, {
      metricResult,
      heartRateDataPoints,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async homeWarmdownStarted(
    workoutId: string,
  ): Promise<void> {
    const request = await this.buildPostRequest(`api/bookings/${workoutId}/homewarmdownstarted`, {
      workoutId,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getHomeWorkoutShareLink(workoutId: string) {
    const request = await this.buildGetRequest(`api/bookings/${workoutId}/workoutStatsMessage`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getWorkoutSession(workoutId: string) {
    const request = await this.buildGetRequest(`api/bookings/${workoutId}`);
    const result = await this.fetch(request.url, request.options);
    const workout = await this.assertHttp200AndGetJsonFromResult(result);
    return workout;
  }

  public async discardWorkout(
    workoutId: string,
    heartRateDataPoints: any,
  ) {
    const request = await this.buildPostRequest(`api/bookings/${workoutId}/discard`, {
      heartRateDataPoints,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getInviteLink(userId: string) {
    const request = await this.buildGetRequest(`api/bookings/${userId}/userInvite`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOpenInviteLinkForWorkout(workoutId: string) {
    const request = await this.buildGetRequest(`api/bookings/${workoutId}/shortlinks`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOpenInviteWorkoutDetails(workoutId: string) {
    const request = await this.buildGetRequest(`api/bookings/openinvites/${workoutId}/details`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async acceptOpenInvite(workoutId: string, useInviterSettings: boolean) {
    const request = await this.buildGetRequest(
      `api/bookings/openinvites/${workoutId}/accept?useInviterSettings=${useInviterSettings}`,
    );
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOpenInviteClassList(workoutId: string) {
    const request = await this.buildGetRequest(`api/bookings/openinvites/${workoutId}/classlist`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getVideoCallAccessToken = async (channelName: string) => {
    const request = await this.buildGetRequest(`api/bookings/videocalls/session/${channelName}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public getAllInstructors = async (): Promise<IInstructorResponse> => {
    const request = await this.buildGetRequest('api/bookings/instructors');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public setDefaultInstructor = async (instructorId: string) => {
    const request = await this.buildPostRequest('api/bookings/instructors/userdefault', { instructorId });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public setWorkoutInstructor = async (workoutId: string, instructorId: string) => {
    const request = await this.buildPostRequest(`api/bookings/instructors/${workoutId}/instructor`, { instructorId });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public updateHomeGroupClassAndGetLeaderboard = async (
    workoutId: string,
    metricResult: any,
    timeLeftInWorkoutMs: number,
  ) => {
    const request = await this.buildPostRequest(`api/bookings/${workoutId}/leaderboard`, {
      metricResult,
      timeLeftInWorkoutMs,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };
}
